import type { ModelBase } from '../model-base';
import type { DataObject } from '../../generic-types';
import { capitalizeFirstLetter } from '../../utils';

export interface HealthCheckIndicatorConfig {
  expectedValue: number | boolean | string;
  type?: string;
  sendToSlack: boolean;
  sendOnWeekend: boolean;
  severityLevel?: HealthCheckNotificationSeverityLevel;
  slackChannelName?: string;
  customConfigs?: DataObject | HealthCheckIndicatorGlobalUrlTriggerConfigs;
}

export interface HealthCheckIndicatorGlobalUrlTriggerConfigs {
  url: string;
  method?: string;
  expectedStatusCode?: number;
  postData?: DataObject;
  axiosRequestConfig?: DataObject;
}

export enum HealthCheckIndicatorProcessorType {
  BASE_SYSTEM_NOTIFICATION = 'baseSystemNotification',
  GLOBAL_URL_TRIGGER = 'globalURLTrigger',
  FILE_IMPORT_COUNTER = 'fileImportCounter',
}

export enum HealthCheckIndicatorType {
  SCHEDULED = 'scheduled',
  SYSTEM = 'system',
}

export enum HealthCheckNotificationDestinationType {
  PORTAL = 'portal',
  EMAIL = 'email',
}

export const notificationDestinationTypeMetaOptions = Object.keys(
  HealthCheckNotificationDestinationType,
).map((key: any) => {
  const value =
    HealthCheckNotificationDestinationType[
      key as keyof typeof HealthCheckNotificationDestinationType
    ];
  return {
    key: value,
    value: capitalizeFirstLetter(value),
  };
});

export enum HealthCheckNotificationSeverityLevel {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

export enum HealthCheckNotificationStatus {
  NEW = 'new',
  READ = 'read',
  RESOLVED = 'resolved',
}
//
export interface HealthCheckTriggerTimeBase {
  previousTriggerDateTime?: string; // iso date-time UTC
  lastTriggerDateTime?: string; // iso date-time UTC
}

export enum HealthCheckFrequencyUnit {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
  MONTHS = 'months',
  YEARS = 'years',
}

export interface HealthCheckSchedule {
  manualNextTriggerDateTime?: string; // iso datetime will use in Org timezone or default one
  triggerFrom?: string; // iso date-time
  triggerTo?: string; // iso date-time
  triggerAtTime?: string; // iso time
  frequency?: number;
  frequencyUnit?: HealthCheckFrequencyUnit;
}

export interface HealthCheckModelBase extends ModelBase {
  organizationId?: string;
}

export interface HealthCheckIndicatorDefinition extends ModelBase {
  key: string;
  processorType: HealthCheckIndicatorProcessorType;
  indicatorType: HealthCheckIndicatorType;
  global: boolean;
  name: string;
  description: string;
  enabled: boolean;
  userRolesToNotify?: string[];
  schedule?: HealthCheckSchedule;
  configs?: HealthCheckIndicatorConfig[];
}

export interface HealthCheckIndicator
  extends HealthCheckIndicatorDefinition,
    HealthCheckTriggerTimeBase,
    HealthCheckModelBase {}

export interface HealthCheckNotification extends HealthCheckModelBase {
  indicatorId: string;
  userRolesToNotify: string[];
  severityLevel: HealthCheckNotificationSeverityLevel;
  title: string;
  content: string;
  status: HealthCheckNotificationStatus;
  sendToSlack: boolean;
  slackChannelName?: string;
  priority?: number; // [min=1, max=10] any other number is considered in this range according border values
  comments?: HealthCheckNotificationMessageComment[];
}

export interface HealthCheckNotificationMessageComment extends ModelBase {
  content: string;
}

export interface HealthCheckEvent<T = DataObject>
  extends Pick<ModelBase, 'createdAt' | 'createdBy'> {
  indicatorId: string;
  data?: T;
}

export interface ScheduleTriggerCheckResult {
  isCanTrigger: boolean;
  disable?: boolean;
}

export interface HealthCheckSlackSecret {
  signingSecret: string;
  botToken: string;
}

export interface HealthCheckSlackMessage {
  title: string;
  content: string;
}
